import React, { useState, useEffect, useRef } from 'react';
import { Copy, Check } from 'lucide-react';
import api from '../lib/api';

const MOON_EMOJIS = {
  nueva: '', creciente_inicial: '', cuarto_creciente: '',
  creciente_gibosa: '', llena: '', menguante_gibosa: '',
  cuarto_menguante: '', menguante_final: '',
};
const MOON_LABELS = {
  nueva: 'Luna nueva', creciente_inicial: 'Creciente', cuarto_creciente: 'Cuarto creciente',
  creciente_gibosa: 'Creciente gibosa', llena: 'Luna llena', menguante_gibosa: 'Menguante gibosa',
  cuarto_menguante: 'Cuarto menguante', menguante_final: 'Menguante',
};

const AltarDelPresente = () => {
  const [altar, setAltar] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get('/campus/altar').then(r => setAltar(r.data)).catch(() => {});
  }, []);

  const copyDecree = () => {
    if (altar?.decree) {
      navigator.clipboard.writeText(altar.decree);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (!altar) return null;
  const { date, element, moon, decree, practice, portal, special, planta, user_name, months_active } = altar;
  const firstName = (user_name || '').split(' ')[0];

  const moonIllum = moon?.illumination ?? 0.5;
  const moonAngle = Math.round(moonIllum * 180);

  return (
    <>
      <link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;1,300;1,400&display=swap" rel="stylesheet" />
      <style>{`
        .altar-font { font-family: 'Cormorant Garamond', Georgia, serif; }
        .altar-lbl { font-family: Georgia, serif; }
        @media (max-width: 900px) {
          .altar-grid-new {
            grid-template-columns: 1fr !important;
            gap: 24px 0 !important;
          }
          .altar-sep-v { display: none !important; }
        }
        @media (max-width: 560px) {
          .altar-grid-new {
            grid-template-columns: 1fr !important;
            text-align: center !important;
          }
          .altar-grid-new > div { text-align: center !important; }
          .altar-right-inner { margin-left: auto !important; margin-right: auto !important; }
        }
      `}</style>

      <div
        className="w-full"
        style={{
          background: '#faf6f0',
          borderTop: '2px solid rgba(180,140,60,0.25)',
          borderBottom: '2px solid rgba(180,140,60,0.15)',
        }}
        data-testid="altar-del-presente"
      >
        {/* Greeting */}
        <div className="text-center pt-6 pb-2">
          <p className="altar-font" style={{ fontSize: 26, fontWeight: 300, color: 'rgba(92,74,42,0.45)', letterSpacing: '0.03em' }}>
            Bienvenid@, <span style={{ color: '#4a3818', fontWeight: 500, fontSize: 28 }}>{firstName}</span>
          </p>
        </div>

        {/* 4-Column Grid: Planta | Calendario | Luna | Tiempo+Decreto */}
        <div
          className="altar-grid-new"
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr auto 1.5fr auto 1.15fr',
            alignItems: 'start',
            padding: '8px 40px 32px',
            gap: '0',
            maxWidth: 1200,
            margin: '0 auto',
          }}
        >
          {/* ═══ COL 1: PLANTA ═══ */}
          <div style={{ paddingTop: 4, position: 'relative' }}>
            <p className="altar-lbl" style={{ fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase', color: 'rgba(140,100,40,0.55)', marginBottom: 10, position: 'relative', zIndex: 2 }}>
              Planta de la semana
            </p>
            {planta ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, position: 'relative' }}>
                <div style={{ flex: 1, position: 'relative', zIndex: 2 }}>
                  <p className="altar-font" style={{ fontSize: 22, fontWeight: 400, fontStyle: 'italic', color: '#3d2b0e', marginBottom: 2 }}>
                    {planta.name}
                  </p>
                  {planta.latin_name && (
                    <p className="altar-lbl" style={{ fontSize: 11, fontStyle: 'italic', color: 'rgba(120,90,30,0.55)', marginBottom: 6 }}>
                      {planta.latin_name}
                    </p>
                  )}
                  <p className="altar-lbl" style={{ fontSize: 12.5, color: 'rgba(140,100,40,0.55)', lineHeight: 1.55 }}>
                    {planta.properties}
                  </p>
                  {planta.tradition && (
                    <span className="altar-lbl" style={{
                      display: 'inline-block', marginTop: 8, padding: '2px 10px', borderRadius: 12,
                      fontSize: 9.5, letterSpacing: '0.08em', textTransform: 'uppercase',
                      color: 'rgba(106,138,106,0.7)', background: 'rgba(106,138,106,0.08)',
                      border: '1px solid rgba(106,138,106,0.2)',
                    }}>
                      {planta.tradition}
                    </span>
                  )}
                </div>
                {planta.image_url ? (
                  <div style={{ flexShrink: 0, position: 'relative', marginRight: -16 }}>
                    <img src={planta.image_url} alt="" aria-hidden="true"
                      style={{ position: 'absolute', top: -15, right: -15, width: 180, height: 180, objectFit: 'contain', opacity: 0.10, pointerEvents: 'none', filter: 'blur(6px)' }} />
                    <img src={planta.image_url} alt={planta.name}
                      style={{ width: 150, height: 150, objectFit: 'contain', filter: 'drop-shadow(0 6px 16px rgba(80,60,20,0.10))', position: 'relative', zIndex: 1 }}
                      data-testid="altar-plant-image" />
                  </div>
                ) : (
                  <div style={{ fontSize: 48, lineHeight: 1, flexShrink: 0, opacity: 0.4 }}>&#127807;</div>
                )}
              </div>
            ) : (
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 36, lineHeight: 1, marginBottom: 8 }}>&#127807;</div>
                <p className="altar-font" style={{ fontSize: 16, fontStyle: 'italic', color: 'rgba(120,90,30,0.4)' }}>
                  Pr&oacute;ximamente...
                </p>
              </div>
            )}
          </div>

          {/* Sep */}
          <div className="altar-sep-v" style={{ width: 1, height: 100, background: 'linear-gradient(to bottom, transparent, rgba(180,140,60,0.3), transparent)', margin: '0 24px', alignSelf: 'center' }} />

          {/* ═══ COL 2: MARCO CENTRAL (Calendario + Luna) ═══ */}
          <div
            style={{
              background: '#fffdf8',
              borderRadius: 12,
              overflow: 'hidden',
              boxShadow: '0 3px 15px rgba(120,90,30,0.08), 0 1px 3px rgba(120,90,30,0.06)',
              border: '1px solid rgba(201,162,39,0.15)',
              alignSelf: 'center',
            }}
            data-testid="altar-calendar-leaf"
          >
            {/* Gold top bar spanning both */}
            <div style={{
              background: 'linear-gradient(135deg, #c9a227 0%, #d4b85c 100%)',
              padding: '6px 0',
              textAlign: 'center',
            }}>
              <p className="altar-lbl" style={{ fontSize: 10, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#fffdf8', fontWeight: 500 }}>
                {date?.weekday}
              </p>
            </div>

            {/* Inner content: Day | Luna side by side */}
            <div style={{ display: 'flex', alignItems: 'center' }}>
              {/* Day side */}
              <div style={{ flex: 1, textAlign: 'center', padding: '10px 20px 10px 24px' }}>
                <p className="altar-font" style={{ fontSize: 52, fontWeight: 300, color: '#3d2b0e', lineHeight: 1 }} data-testid="altar-date">
                  {date?.day}
                </p>
                <p className="altar-lbl" style={{ fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'rgba(140,100,40,0.6)', marginTop: 4 }}>
                  {date?.month_name} {date?.year}
                </p>
              </div>

              {/* Vertical divider inside */}
              <div style={{ width: 1, height: 70, background: 'linear-gradient(to bottom, transparent, rgba(201,162,39,0.2), transparent)', flexShrink: 0 }} />

              {/* Moon side */}
              <div style={{ flex: 1, textAlign: 'center', padding: '10px 24px 10px 20px' }}>
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 6 }}>
                  <div style={{
                    width: 42, height: 42, borderRadius: '50%',
                    background: `radial-gradient(circle at ${moonAngle > 90 ? 30 : 70}% 50%, #f5e6c8 0%, rgba(180,150,80,0.2) 100%)`,
                    boxShadow: '0 0 12px 3px rgba(212,175,55,0.12), inset 0 0 8px rgba(180,140,60,0.15)',
                    border: '1px solid rgba(201,162,39,0.2)',
                  }} />
                </div>
                <p className="altar-font" style={{ fontSize: 16, fontWeight: 400, fontStyle: 'italic', color: '#3d2b0e', marginBottom: 2 }}>
                  {MOON_LABELS[moon?.name] || 'Luna'}
                </p>
                <p className="altar-lbl" style={{ fontSize: 11, color: 'rgba(140,100,40,0.55)' }}>
                  {moon?.quality}
                </p>
              </div>
            </div>

            {/* Bottom element line */}
            <div style={{
              borderTop: '1px solid rgba(201,162,39,0.12)',
              padding: '7px 12px',
              textAlign: 'center',
            }}>
              <p className="altar-lbl" style={{ fontSize: 10.5, color: 'rgba(140,100,40,0.50)' }}>
                {element?.name} &middot; {element?.quality?.split(',')[0]?.trim().toLowerCase()}
                {(portal?.active || special) && (
                  <span style={{ marginLeft: 10, padding: '2px 10px', borderRadius: 10, fontSize: 9, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(140,100,40,0.75)', background: 'rgba(180,140,60,0.1)', border: '1px solid rgba(180,140,60,0.25)' }} data-testid="altar-portal">
                    {special ? special.name : `Portal ${portal.portal}`}
                  </span>
                )}
              </p>
            </div>
          </div>

          {/* Sep */}
          <div className="altar-sep-v" style={{ width: 1, height: 100, background: 'linear-gradient(to bottom, transparent, rgba(180,140,60,0.3), transparent)', margin: '0 24px', alignSelf: 'center' }} />

          {/* ═══ COL 4: TIEMPO + DECRETO ═══ */}
          <div style={{ textAlign: 'right', paddingTop: 4 }}>
            <p className="altar-lbl" style={{ fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase', color: 'rgba(140,100,40,0.55)', marginBottom: 10 }}>
              Tiempo en el jard&iacute;n
            </p>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'flex-end', gap: 8, marginBottom: 16 }}>
              <span className="altar-font" style={{ fontSize: 32, fontWeight: 300, color: '#3d2b0e', letterSpacing: '0.03em', fontVariantNumeric: 'tabular-nums' }} data-testid="altar-timer">
                {months_active || 1}
              </span>
              <span className="altar-lbl" style={{ fontSize: 14, color: 'rgba(140,100,40,0.55)' }}>
                {(months_active || 1) === 1 ? 'mes' : 'meses'}
              </span>
            </div>

            <p className="altar-lbl" style={{ fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'rgba(140,100,40,0.5)', marginBottom: 6 }}>
              Decreto del d&iacute;a
            </p>
            <div className="altar-right-inner" style={{ maxWidth: 290, marginLeft: 'auto' }}>
              <p className="altar-font" style={{ fontSize: 16, fontStyle: 'italic', fontWeight: 400, color: '#4a3818', lineHeight: 1.55 }} data-testid="altar-decree">
                &ldquo;{decree}&rdquo;
                <button
                  onClick={copyDecree}
                  className="ml-2 inline-flex align-middle opacity-30 hover:opacity-70 transition-opacity"
                  data-testid="altar-copy-decree"
                  title="Copiar decreto"
                >
                  {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                </button>
              </p>
              <p className="altar-lbl" style={{ fontSize: 12, color: 'rgba(120,90,30,0.55)', marginTop: 10, lineHeight: 1.5, fontStyle: 'italic' }}>
                {practice}
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AltarDelPresente;
